// @ts-strict-ignore
import { MultiSelect } from '@usealloy/component-library';
import React, {
  useCallback, useMemo, useRef,
} from 'react';

import type { Category, DataRoute } from 'flow/store/engine/types';
import { ActionType } from 'flow/store/engine/constants';
import {
  selectActions,
  selectFocusedAction,
  selectNodeLineageForAnchorNode,
  selectServices,
} from 'flow/store/redux/selectors';
import { useAppSelector } from '~/store/reduxHooks';
import type { DropdownOption } from '../../types';
import { MULTISELECT_PROPS } from '../../../constants';
import { getActionName } from '~/dashboard/views/workflows/service/actions';
import { generatePublicId } from 'flow/service/publicIds';
import { StyledRoutingServiceName } from './styles';


interface RoutingServiceDropdownProps {
  mode: 'incoming' | 'outgoing';
  category: Category;
  isEditing: boolean;
  dataRouteForCategory: DataRoute;
  setDataRouteForCategory: React.Dispatch<React.SetStateAction<DataRoute>>;
}

export default function RoutingServiceDropdown({
  mode,
  category,
  isEditing,
  dataRouteForCategory,
  setDataRouteForCategory,
}: RoutingServiceDropdownProps) {
  const actions = useAppSelector(selectActions);
  const services = useAppSelector(selectServices);
  const focusedAction = useAppSelector(selectFocusedAction);
  const anchorNodeLineage = useAppSelector(selectNodeLineageForAnchorNode);


  const multiSelectRef = useRef(null);

  const serviceCategoryType = mode === 'incoming'
    ? 'response'
    : 'request';

  const routingServiceAction = useMemo(() => {
    if (!dataRouteForCategory) return null;
    return actions[
      mode === 'incoming'
        ? dataRouteForCategory.sourceId
        : dataRouteForCategory.targetId
    ];
  }, [actions, mode, dataRouteForCategory]);

  const options = React.useMemo<DropdownOption[]>(() => (
    Object.values(actions)
      .filter(action => action.type === ActionType.SERVICE
        && action.publicId !== focusedAction.publicId
        && services[action.service.internalName].categories[serviceCategoryType]
          .find(svcCategory => svcCategory.name === category.name))
      .filter(action => (
        (
          !anchorNodeLineage?.extendedOutgoers?.has(action.publicId)
          && !anchorNodeLineage.directOutgoers.has(action.publicId)
        )
        || action.publicId === routingServiceAction?.publicId
      ))
      .map(action => ({
        label: getActionName(action),
        value: action.publicId,
      }))
      .sort((a, b) => a.label.localeCompare(b.label))
  ), [
    actions,
    services,
    serviceCategoryType,
    category,
    anchorNodeLineage,
    focusedAction,
    routingServiceAction,
  ]);

  const selectedOption = useMemo(() => {
    if (dataRouteForCategory) {
      return options?.find(option => routingServiceAction.publicId === option.value);
    }

    if (mode === 'outgoing') {
      return {
        label: '--',
        value: null,
      };
    }

    return {
      label: 'Supplied Data',
      value: null,
    };
  }, [dataRouteForCategory, mode, options, routingServiceAction]);

  const onChange = useCallback(inputValue => {
    const keyToUpdateActionId = mode === 'incoming' ? 'sourceId' : 'targetId';

    if (dataRouteForCategory) {
      return setDataRouteForCategory({
        ...dataRouteForCategory,
        [keyToUpdateActionId]: inputValue.value,
      });
    }

    const sourceAndTarget = mode === 'incoming'
      ? {
        sourceId: inputValue.value,
        targetId: focusedAction.publicId,
      }
      : {
        sourceId: focusedAction.publicId,
        targetId: inputValue.value,
      };
    const newDataRouteId = generatePublicId('dataroute');
    return setDataRouteForCategory({
      publicId: newDataRouteId,
      ...sourceAndTarget,
      category,
      attribute: {
        id: null,
        token: null,
        displayName: '',
        attributeName: '',
        multiLevelAttributeFilter: {
          filterComponents: [],
        },
      },
      aggregationComponents: [],
    });
  }, [mode, dataRouteForCategory, focusedAction, setDataRouteForCategory, category]);

  const onEscKey = (_: string, e: React.KeyboardEvent<HTMLInputElement>) => {
    multiSelectRef?.current?.blur();
    e.preventDefault();
    e.stopPropagation();
  };

  return !isEditing ? (
    <StyledRoutingServiceName serviceSelected={Boolean(selectedOption.value)}>
      {selectedOption.label}
    </StyledRoutingServiceName>
  ) : (
    <MultiSelect
      {...MULTISELECT_PROPS}
      openMenuOnFocus
      options={options}
      onChange={onChange}
      selectedOptions={selectedOption}
      noOptionsMessage={() => 'No options'}
      onKeyDownHandlers={{
        Escape: onEscKey,
      }}
      ref={multiSelectRef}
    />
  );
}
